import { useState, type ReactNode } from 'react';
import { AlertTriangle, BadgeIndianRupee, CalendarClock, Download, FileText, Percent, Receipt } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/ui/MetricTile';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Table, type Column } from '@/components/ui/Table';
import {
  FeeReceiptPrint,
  type PrintableReceipt,
} from '@/features/institute/fees/components/FeeReceiptDocument';
import {
  FEE_METHOD_LABEL,
  FEE_STATUS_LABEL,
  FEE_STATUS_TONE,
} from '@/features/institute/fees/constants';
import { formatInstituteDate } from '@/features/teacher/utils';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FeeReceipt } from '@/types/fees';
import type { MyFeeAccount } from '@/types/student';

interface FeeReceiptsProps {
  account: MyFeeAccount | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
}

/**
 * What the family has been charged this session, and what has been paid.
 *
 * Read from `GET /student/fees` on the child's panel and
 * `GET /parent/children/:uuid/fees` on the parent's — both the office's own
 * `getStudentFeeAccount`, trimmed for a family by `readFeeAccountForFamily`. That matters
 * more here than anywhere else in the panel: a fee screen that does its own
 * arithmetic is a fee screen that can disagree with the receipt in a parent's
 * file, and the argument that follows happens at a counter.
 *
 * This component used to take five props with defaults — `'₹45,000'`,
 * `'REC-2026-4910'`, `'Paid ₹45,000 via Online UPI (Ref: 4920194819)'` — and
 * was rendered with none of them, so every family on the platform was shown the
 * same settled account and the same invented receipt number. It was commented
 * out of the dashboard, which is the only reason nobody had acted on it.
 *
 * **Nothing here is an instruction to pay.** The panel shows the ledger; how a
 * family settles it is the office's arrangement, and inventing a "Pay now"
 * button would promise a payment route this product does not have.
 */
/**
 * The line under a tile's figure, at reading size and in body ink. The tile's
 * own hint style is a small tinted caption, which on this screen carried the
 * sums a family checks their bill against and read too faint for that.
 */
function TileHint({ children }: { children: ReactNode }) {
  return <span className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-300">{children}</span>;
}

export function FeeReceipts({ account, isLoading, error, onRetry }: FeeReceiptsProps) {
  /** The receipt on its way to the printer (or to "Save as PDF"), or null. */
  const [printing, setPrinting] = useState<PrintableReceipt | null>(null);

  if (isLoading && !account) {
    return (
      <div className="card p-5 rounded-[20px]">
        <SkeletonLines count={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-5 rounded-[20px] flex flex-col items-start gap-3">
        <Alert tone="critical" title="Could not load your fees">
          {error.message}
        </Alert>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (!account) return null;

  /*
    "Nothing agreed yet" is not "nothing owed".

    A college student, or a child admitted this week, has no account until the
    office opens one. Showing them ₹0 outstanding would be telling them they are
    square with a school that has not yet worked out what to charge.
  */
  if (!account.hasAccount) {
    return (
      <div className="card p-6 rounded-[20px]">
        <EmptyState
          icon={<BadgeIndianRupee className="h-5 w-5" />}
          title="No fees have been set yet"
          description={`The office has not recorded a fee for ${account.academicYear.name}. Nothing is outstanding, and nothing is due — ask at the school office if you were expecting a bill.`}
        />
      </div>
    );
  }

  const isSettled = account.status === 'PAID';
  const hasConcession = Number(account.concession) > 0;

  const columns: Column<FeeReceipt>[] = [
    {
      key: 'receipt',
      header: 'Receipt',
      render: (receipt) => (
        <span className="flex flex-col min-w-0">
          <span
            className={cn(
              'font-mono text-sm sm:text-[15px] font-bold text-slate-900 dark:text-slate-100',
              // Struck through rather than hidden: a cancelled receipt stays on
              // the ledger, and a family that holds the paper one must be able
              // to find it here and see that it was reversed.
              receipt.isCancelled && 'line-through text-slate-400 dark:text-slate-500',
            )}
          >
            {receipt.receiptNumber}
          </span>
          <span className="mt-0.5 text-xs font-medium text-slate-600 dark:text-slate-300">
            Taken by {receipt.collectedBy}
          </span>
        </span>
      ),
    },
    {
      key: 'paidOn',
      header: 'Paid on',
      render: (receipt) => (
        <span className="whitespace-nowrap text-sm font-semibold text-slate-700 dark:text-slate-200">
          {formatInstituteDate(receipt.paidOn)}
        </span>
      ),
    },
    {
      key: 'method',
      header: 'Method',
      hideOnMobile: true,
      render: (receipt) => (
        <span className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{FEE_METHOD_LABEL[receipt.method]}</span>
          {receipt.reference && (
            <span className="mt-0.5 truncate font-mono text-xs font-medium text-slate-600 dark:text-slate-300" title={receipt.reference}>
              {receipt.reference}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (receipt) => (
        <span
          className={cn(
            'text-sm sm:text-base font-bold tabular-nums text-slate-900 dark:text-white',
            receipt.isCancelled && 'line-through text-slate-400 dark:text-slate-500',
          )}
        >
          {formatCurrency(receipt.amount)}
        </span>
      ),
    },
    {
      key: 'state',
      header: '',
      align: 'right',
      render: (receipt) =>
        receipt.isCancelled ? (
          // The reason goes on a wrapper: `Badge` takes no title, and the
          // family is owed the reason the receipt was reversed.
          <span title={receipt.cancellationReason ?? undefined}>
            <Badge tone="critical">Cancelled</Badge>
          </span>
        ) : (
          // The office's own receipt layout, so the family's copy matches the
          // one handed over at the counter. Downloading is the print dialog's
          // "Save as PDF" — there is no second, PDF-only layout to keep in step.
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            aria-label={`Download receipt ${receipt.receiptNumber}`}
            title="Choose “Save as PDF” in the print dialog"
            onClick={() =>
              setPrinting({
                receipt,
                student: account.student,
                sessionName: account.academicYear.name,
              })
            }
          >
            Download
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* The three figures a family actually asks for, and the session they
          belong to — a receipt from last year is not this year's balance. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <MetricTile
          tone="blue"
          icon={<BadgeIndianRupee className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Payable"
          value={formatCurrency(account.payable)}
          hint={
            <TileHint>
              {hasConcession
                ? `${formatCurrency(account.charged)} less ${formatCurrency(account.concession)}`
                : account.academicYear.name}
            </TileHint>
          }
        />
        <MetricTile
          tone="emerald"
          icon={<Receipt className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Paid so far"
          value={formatCurrency(account.collected)}
          hint={
            <TileHint>
              {`${account.receipts.filter((receipt) => !receipt.isCancelled).length} receipts`}
            </TileHint>
          }
        />
        <MetricTile
          tone={isSettled ? 'emerald' : 'amber'}
          icon={<CalendarClock className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Still to pay"
          value={formatCurrency(account.balance)}
          hint={
            <TileHint>
              {isSettled
                ? 'Nothing outstanding'
                : account.dueDate
                  ? `Due by ${formatInstituteDate(account.dueDate)}`
                  : 'No due date set'}
            </TileHint>
          }
        />
      </div>

      {/* Only where the office actually reduced the fee, with the reason it
          gave when it gave one — never a stand-in sentence in its place. */}
      {hasConcession && (
        <div className="card rounded-[16px] p-4 flex items-start gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-field bg-accent-soft text-accent"
            aria-hidden
          >
            <Percent className="h-4 w-4" />
          </span>
          <span className="flex flex-col min-w-0">
            <span className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
              {formatCurrency(account.concession)} concession
            </span>
            {account.concessionReason && (
              <span className="mt-0.5 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300">{account.concessionReason}</span>
            )}
          </span>
        </div>
      )}

      {/* The two heads the account is kept in, because a family paying in
          instalments is usually square on one and behind on the other. */}
      <div className="card overflow-hidden rounded-[20px]">
        <div className="border-b border-line bg-surface-2 px-4 py-3.5 flex items-center justify-between gap-3">
          <h3 className="text-base font-bold text-slate-800 dark:text-white">This session's bill</h3>
          <Badge tone={FEE_STATUS_TONE[account.status]} withDot>
            {FEE_STATUS_LABEL[account.status]}
          </Badge>
        </div>

        <dl className="divide-y divide-line">
          {[
            { key: 'tuition', label: 'Tuition', head: account.tuition },
            { key: 'books', label: 'Books and materials', head: account.books },
            { key: 'other', label: 'Other fees', head: account.other },
          ].map(({ key, label, head }) => (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3.5">
              <dt className="flex flex-col min-w-0">
                <span className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100">{label}</span>
                <span className="mt-0.5 text-xs sm:text-[13px] font-medium text-slate-600 dark:text-slate-300">
                  {formatCurrency(head.fee)} charged
                  {Number(head.discount) > 0 ? `, ${formatCurrency(head.discount)} off` : ''} ·{' '}
                  {formatCurrency(head.paid)} paid
                </span>
              </dt>
              <dd
                className={cn(
                  'shrink-0 text-sm sm:text-base font-bold tabular-nums',
                  Number(head.balance) > 0
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {Number(head.balance) > 0 ? `${formatCurrency(head.balance)} due` : 'Settled'}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* ---------------- receipts ---------------- */}
      <div className="card overflow-hidden rounded-[20px]">
        <div className="border-b border-line bg-surface-2 px-4 py-3.5">
          <h3 className="text-base font-bold text-slate-800 dark:text-white">Receipts</h3>
          <p className="mt-1 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300">
            Every payment the office has recorded against your account, newest first.
          </p>
        </div>

        {/*
          Money collected with no receipt behind it is a real state, and it is
          said rather than drawn as an empty table. It happens where an opening
          balance was carried across; the office can print the paper receipt.
        */}
        {account.receipts.length === 0 && Number(account.collected) > 0 && (
          <div className="px-4 pt-4">
            <Alert tone="warning" title="Payments recorded without a receipt">
              {formatCurrency(account.collected)} is shown against your account, but no individual
              receipt has been entered for it. The school office can print one for you.
            </Alert>
          </div>
        )}

        <Table
          variant="plain"
          columns={columns}
          rows={account.receipts}
          rowKey={(receipt) => receipt.uuid}
          empty={
            <EmptyState
              icon={<FileText className="h-5 w-5" />}
              title="No receipts yet"
              description={
                Number(account.collected) > 0
                  ? 'Ask at the school office for a printed receipt.'
                  : 'When a payment is recorded against your account, its receipt appears here.'
              }
            />
          }
        />
      </div>

      {/* A balance is a fact, not an accusation — and the office, not this
          screen, is where a family sorts one out. */}
      {!isSettled && (
        <p className="flex items-start gap-2 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
          Payments are recorded by the school office. If a payment you have made is not listed
          here, take the receipt to the office and they will enter it.
        </p>
      )}

      <FeeReceiptPrint target={printing} onFinished={() => setPrinting(null)} />
    </div>
  );
}
